import { BaseRemoteService } from "@ofqual/portal-core/core/dataAccess";
import {
  Opts,
  Model,
  PagedResponse,
  QueryParams,
  ValidatedResponse,
  PatchWithMeta,
} from "@ofqual/portal-core/core/types";

import {
  ExpertsApplyModel,
  Recipient,
  fromApiModel,
} from "../domain/ExpertsApplyFormModel";
import { paramsToSearchString } from "@ofqual/portal-core/core/utils/urlHelper";
import { PagedResponseTo } from "@ofqual/portal-core/core/utils/pagedResponseHelpers";
import { QualificationModel } from "../domain/QualificationModel";
import { getValidationError } from "@ofqual/portal-core/core/utils/validationHelper";
import { MessageThread } from "@ofqual/portal-activity-feed/activityfeed/domain/PublicMessageModel";
import {
  ApprovalSubject,
  AssessmentEvaluation,
  Evaluation,
  IndustryEvaluation,
  TeachingEvaluation,
} from "../domain/ApprovalSubject";
import { ChangeApplicationStatusModel } from "../presentation/types";

export class ExpertsApplyService extends BaseRemoteService {
  public async get(id: string, opts: Opts): Promise<ExpertsApplyModel> {
    const url = `${this.apiRoot}/expertApplyById?id=${id}`;
    const response = await super.getRequest<Model>(url, opts);
    return fromApiModel(response);
  }

  public async search(
    params: QueryParams,
    opts: Opts
  ): Promise<PagedResponse<ExpertsApplyModel>> {
    const url = `${this.apiRoot}/experts/${paramsToSearchString(params)}`;
    const response = await super.getRequest<PagedResponse<Model>>(url, opts);
    return PagedResponseTo<ExpertsApplyModel>(response, fromApiModel);
  }

  public async downloadQualificationAttachment(
    id: string,
    qualId: string,
    blobId: string,
    opts: Opts
  ): Promise<Blob> {
    const url = `${this.apiRoot}/expertApply/${id}/qualification/${qualId}/download/${blobId}`;
    return await super.getRequestBlob(url, opts);
  }

  public async updateApplicationStatus(
    id: string,
    applicationStatus: ChangeApplicationStatusModel,
    opts: Opts
  ): Promise<ValidatedResponse<ExpertsApplyModel>> {
    const url = `${this.apiRoot}/expertApply/${id}/status`;
    const response: ExpertsApplyModel = await super.postRequest(
      url,
      applicationStatus,
      opts
    );
    return { isValid: true, data: response };
  }

  public async downloadActivityAttachment(
    id: string,
    activityId: string,
    blobId: string,
    opts: Opts
  ): Promise<Blob> {
    const url = `${this.apiRoot}/activity/${id}/documents/${activityId}/download/${blobId}`;
    return await super.getRequestBlob(url, opts);
  }

  public async sendPublicMessage(
    id: string,
    files: File[],
    subject: string,
    message: string,
    threadId: string,
    opts: Opts
  ): Promise<unknown> {
    const url =
      threadId === undefined
        ? `${this.apiRoot}/activity/send-message/${id}`
        : `${this.apiRoot}/activity/send-message/${id}?messageThread=${threadId}`;
    const messageData = {
      id,
      subject,
      message,
    };
    const messageSerialized = JSON.stringify(messageData);
    return await super.postRequestFormData(
      url,
      { messageSerialized, files, messageThread: threadId },
      opts
    );
  }

  public async getMessageThreads(
    citizenId: string,
    opts: Opts
  ): Promise<MessageThread[]> {
    const url = `${this.apiRoot}/activity/message-threads/${citizenId}`;
    return await super.getRequest<MessageThread[]>(url, opts);
  }

  public async countUnreadMessages(
    citizenId: string,
    type: number,
    opts: Opts
  ): Promise<number> {
    const url = `${this.apiRoot}/activity/unread-message-count/${citizenId}/message-type/${type}`;
    const data = await super.getRequest<number>(url, opts);
    return data;
  }

  public async postAttachments(
    id: string,
    qualifications: QualificationModel[],
    files: File[],
    opts: Opts
  ): Promise<unknown> {
    const url = `${this.apiRoot}/expertApply/${id}/qualification-attachments`;
    const qualificationsSerialized = JSON.stringify(qualifications);

    return await super.postRequestFormData(
      url,
      {
        qualificationsSerialized: qualificationsSerialized,
        files: files,
      },
      opts
    );
  }

  public async sendPublicMessageRead(
    messageId: string,
    opts: Opts
  ): Promise<unknown> {
    const url = `${this.apiRoot}/activity/message-read/${messageId}`;
    return await super.postRequest(url, {}, opts);
  }

  public async addCaseNote(
    id: string,
    caseFiles: File[],
    caseNote: string,
    subject: string,
    opts: Opts
  ): Promise<unknown> {
    let url = `${this.apiRoot}/activity/${id}/casenote`;

    return await super.postRequestFormData(
      url,
      { caseNote, caseFiles, subject },
      opts
    );
  }
  public async addInternalMessage(
    id: string,
    files: File[],
    subject: string,
    message: string,
    recipients: Recipient[],
    opts: Opts
  ): Promise<unknown> {
    const url = `${this.apiRoot}/activity/${id}/internal-message`;
    const messageData = { id, subject, message, recipients };
    const serialisedInternalMessage = JSON.stringify(messageData);
    return await super.postRequestFormData(
      url,
      { id, serialisedInternalMessage, files },
      opts
    );
  }

  public async update(
    id: string,
    patchDocument: PatchWithMeta<ExpertsApplyModel>,
    opts: Opts
  ): Promise<ValidatedResponse<ExpertsApplyModel>> {
    const url = `${this.apiRoot}/expertApply/${id}`;
    try {
      const response = await this.patchRequest<
        ExpertsApplyModel,
        PatchWithMeta<ExpertsApplyModel>
      >(url, patchDocument, opts);
      return {
        isValid: true,
        data: fromApiModel(response),
      };
    } catch (err) {
      const validationError = getValidationError(err);
      if (validationError.isValidationError) {
        return {
          isValid: false,
          error: validationError.error,
        };
      }
      throw err;
    }
  }

  public async updateAssessmentApprovals(
    id: string,
    approvalSubjects: ApprovalSubject[],
    assessmentEvaluation: AssessmentEvaluation | undefined,
    send:boolean,
    opts: Opts
  ): Promise<unknown> {
    let url = `${this.apiRoot}/expertApply/AssessmentExperience/${id}?send=${send}`;
    let request: any = { approvalSubjects };
    if (assessmentEvaluation !== undefined) {
      request = { approvalSubjects, assessmentEvaluation };
    }

    return await super.postRequest(url, request, opts);
  }

  public async updateTeachingApprovals(
    id: string,
    approvalSubjects: ApprovalSubject[],
    teachingEvaluation: TeachingEvaluation | undefined,
    send : boolean,
    opts: Opts
  ): Promise<unknown> {
    let url = `${this.apiRoot}/expertApply/TeachingExperience/${id}?send=${send}`;
    let request: any = { approvalSubjects };
    if (teachingEvaluation !== undefined) {
      request = { approvalSubjects, teachingEvaluation };
    }

    return await super.postRequest(url, request, opts);
  }

  public async updateIndustryApprovals(
    id: string,
    approvalSubjects: ApprovalSubject[],
    industryEvaluation: IndustryEvaluation | undefined,
    send:boolean,
    opts: Opts
  ): Promise<unknown> {
    let url = `${this.apiRoot}/expertApply/IndustryExperience/${id}?send=${send}`;
    let request: any = { approvalSubjects };
    if (industryEvaluation !== undefined) {
      request = { approvalSubjects, industryEvaluation };
    }

    return await super.postRequest(url, request, opts);
  }

  public async getAllEvaluations(
    citizenId: string,
    opts: Opts
  ): Promise<Evaluation> {
    const url = `${this.apiRoot}/expertApply/AllExperience/${citizenId}`;
    const data = await super.getRequest<Evaluation>(url, opts);
    return data;
  }
}
